/**
 * Zone-aware recurrence anchoring.
 *
 * rrule.js advances in fixed intervals from its `dtstart`. Handed a UTC
 * instant it therefore adds exactly 7×24h per weekly step — which is WRONG
 * across a DST transition: a 09:00 series in America/Denver silently becomes
 * 08:00 for every occurrence after the November change, and 10:00 after March.
 * It drifts twice a year, in the UI, in sync comparisons, and in published
 * feeds a phone re-reads forever.
 *
 * The fix is to expand on the FLOATING wall clock (the local time the user
 * sees) and re-anchor each occurrence back to a UTC instant through the zone,
 * so the wall time is what stays fixed and the offset moves instead.
 *
 * Extracted from the expansion module so every recurrence evaluator in this
 * package (expansion, reconciliation, series-end) anchors zones identically.
 */

/** Sentinel for floating local time (RFC 5545 §3.3.5). */
export const FLOATING_TZ = 'FLOATING';

/** Wall-clock parts of a UTC instant as observed in `tz`. */
function wallPartsInZone(instant: Date, tz: string): Record<string, number> {
  const parts: Record<string, number> = {};
  for (const { type, value } of formatterFor(tz).formatToParts(instant)) {
    if (type !== 'literal') parts[type] = Number(value);
  }
  // Intl reports midnight as hour 24 in some locales/engines.
  if (parts.hour === 24) parts.hour = 0;
  return parts;
}

const ZONE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = ZONE_FORMATTERS.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    ZONE_FORMATTERS.set(tz, f);
  }
  return f;
}

/** A floating Date whose UTC fields carry the wall clock seen in `tz`. */
export function toFloating(instant: Date, tz: string): Date {
  const p = wallPartsInZone(instant, tz);
  return new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second));
}

/** The UTC instant whose wall clock in `tz` equals the floating Date's fields. */
export function fromFloating(floating: Date, tz: string): Date {
  formatterFor(tz);
  const target = floating.getTime();
  let ms = target;
  // Converge: the first correction can itself cross a transition, so re-check.
  for (let i = 0; i < 3; i += 1) {
    const seen = toFloating(new Date(ms), tz).getTime();
    const delta = target - seen;
    if (delta === 0) break;
    ms += delta;
  }
  return new Date(ms);
}

/** Zones needing no re-anchoring: fixed offset, or deliberately unanchored. */
export function hasFixedOffset(tz: string | null | undefined): boolean {
  return !tz || tz === FLOATING_TZ || tz === 'UTC' || tz === 'Etc/UTC';
}

/** Widens the floating search window so an occurrence near the range edge is
 *  not lost to the offset difference (max ~26h worldwide) before re-anchoring. */
export const FLOATING_RANGE_PAD_MS = 2 * 24 * 60 * 60 * 1000;
